import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
// @mui
import List from '@mui/material/List';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
// components
import Scrollbar from 'src/components/scrollbar';
import FileManagerInvitedItem from './file-manager-invited-item';
import { useSnackbar } from 'src/components/snackbar';
import { useShareFolderMutation } from 'src/store/Reducer/folder';
import { CircularProgress } from '@mui/material';
import Iconify from 'src/components/iconify';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useBackToClose } from 'src/hooks/use-back-to-close';

export default function FileManagerShareDialog({
  shared,
  selectedImages,
  open,
  onClose,
  setSelectedImages,
  fromCategory,
  fromProject,
  totalImages,
  ...other
}) {
  useBackToClose(open, onClose); // browser / mouse Back closes this dialog
  const { enqueueSnackbar } = useSnackbar();
  const [shareType, setShareType] = useState('email');
  const [currentInput, setCurrentInput] = useState({ value: '', error: '' });
  const [selectedContacts, setSelectedContacts] = useState([]);

  const hasShared = shared && !!shared.length;
  const [shareFolder, { isLoading }] = useShareFolderMutation();

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  

  const validatePhone = (phone) => {
    const phoneStr = String(phone || '');
    if (!phoneStr) return false;
    const parsed = parsePhoneNumberFromString(phoneStr, 'US');
    return parsed?.isValid() ?? false;
  };

  const handleShareTypeChange = (event) => {
    const newType = event.target.value;
    setShareType(newType);
    setSelectedContacts([]);
    setCurrentInput({ value: '', error: '' });
  };

  const handleInputChange = (value) => {
    setCurrentInput((prev) => {
      const newInput = { ...prev, value };
      if (!value) {
        newInput.error = '';
      } else if (shareType === 'email' && !validateEmail(value)) {
        newInput.error = 'Please enter a valid email address';
      } else if (shareType === 'phone' && !validatePhone(value)) {
        newInput.error = 'Please enter a valid phone number (e.g. +1 201 568 9000)';
      } else {
        newInput.error = '';
      }
      
      return newInput;
    });
  };

  const handleAddToSelected = () => {
    if (currentInput.value && !currentInput.error) {
      const value = shareType === 'phone' ?  parsePhoneNumberFromString(currentInput.value, 'US').number : currentInput.value;
      setSelectedContacts([
        ...selectedContacts, 
        { type: shareType, value }
      ]);
      setCurrentInput({ value: '', error: '' });
    }
  };

  const handleRemoveSelected = (index) => {
    setSelectedContacts(selectedContacts.filter((_, i) => i !== index));
  };

  const handleSendInvite = async () => {
    let contactsToSend = [];
    if (selectedContacts.length > 0) {
      contactsToSend = selectedContacts.map(c => c.value);
    } else if (currentInput.value && !currentInput.error) {
      const value = shareType === 'phone' ? currentInput.value : currentInput.value;
      contactsToSend = [value];
    }
    if (contactsToSend.length === 0) {
      enqueueSnackbar('Please enter a valid contact to share', { variant: 'error' });
      return;
    }
    if (currentInput.value && !currentInput.error && !contactsToSend.includes(currentInput.value)) {
      const value = shareType === 'phone' ? currentInput.value : currentInput.value;
      if (!contactsToSend.includes(value)) {
        contactsToSend.push(value);
      }
    }
    try {
      const selectedIds = selectedImages?.map((img) => img?.id);
      const typeValue = shareType === 'email' ? 1 : 2;
      let payload = {};
      if (fromProject) {
        payload = {
          ids: selectedIds,
          share_category: 'files',
          ...(shareType === 'email' ? { email: contactsToSend } : { phone: contactsToSend }),
          type: typeValue,
        };
      } else if (selectedIds?.length == totalImages?.length) {
        payload = {
          ids: [],
          share_category: fromCategory ? 'category' : 'folder',
          ...(fromCategory
            ? { category_id: selectedImages[0]?.category_id }
            : { folder_id: totalImages[0]?.folder_id }),
          ...(shareType === 'email' ? { email: contactsToSend } : { phone: contactsToSend }),
          type: typeValue,
        };
      } else {
        payload = {
          ids: selectedIds,
          share_category: 'files',
          ...(shareType === 'email' ? { email: contactsToSend } : { phone: contactsToSend }),
          type: typeValue,
        };
      }
      await shareFolder(payload).unwrap();
      enqueueSnackbar('Share Files successfully!', { variant: 'success' });
      setSelectedImages([]);
      setCurrentInput({ value: '', error: '' });
      setSelectedContacts([]);
      onClose();
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message || 'Failed to send files, please try again.',
        { variant: 'error' }
      );
    }
  };


  useEffect(() => {
    if (!open) {
      setShareType('email');
      setCurrentInput({ value: '', error: '' });
      setSelectedContacts([]);
    }
  }, [open]);
  

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose} {...other}>
      <DialogTitle sx={{ textAlign: 'center' }}>Share Files</DialogTitle>

      <DialogContent sx={{ overflow: 'unset' }}>
        <RadioGroup
          row
          value={shareType}
          onChange={handleShareTypeChange}
          sx={{ mb: 2, justifyContent: 'center' }}
        >
          <FormControlLabel 
            value="email" 
            control={<Radio />} 
            label="Share by Email" 
          />
          <FormControlLabel 
            value="phone" 
            control={<Radio />} 
            label="Share by Phone" 
          />
        </RadioGroup>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 , textAlign : 'center' }}>
          {shareType === 'email' 
            ? 'Enter email addresses'
            : 'Enter phone numbers'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 1 }}>
          <TextField
            fullWidth
            label={shareType === 'email' ? 'Email address' : 'Phone number'}
            placeholder={shareType === 'email' ? 'e.g. user@example.com' : 'e.g. +1 201 568 9000'}
            value={currentInput.value}
            onChange={(e) => handleInputChange(e.target.value)}
            error={!!currentInput.error}
            helperText={currentInput.error}
          />
          
            <IconButton
              onClick={handleAddToSelected}
              sx={{ 
                mt: 1,
                bgcolor: 'error.main',
                color: 'white',
                '&:hover': { bgcolor: 'error.dark' }
              }}
            >
              <Iconify icon="eva:plus-fill" />
            </IconButton>
        </Box>

        {selectedContacts.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Selected ({selectedContacts.length}):
            </Typography>
            <Box 
              sx={{ 
                p: 2, 
                bgcolor: 'grey.100', 
                borderRadius: 1,
                maxHeight: 150,
                overflowY: 'auto'
              }}
            >
              {selectedContacts.map((contact, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                    p: 1.5,
                    bgcolor: 'white',
                    borderRadius: 1,
                    '&:last-child': { mb: 0 }
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {contact.type === 'email' ? 'Email' : 'Phone'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {contact.value}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSelected(index)}
                  >
                    <Iconify icon="eva:close-fill" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {hasShared && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Previously Shared:
            </Typography>
            <Scrollbar sx={{ maxHeight: 60 * 4 }}>
              <List disablePadding>
                {shared.map((person) => (
                  <FileManagerInvitedItem key={person.id} person={person} shareButton />
                ))}
              </List>
            </Scrollbar>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSendInvite}
          disabled={
            isLoading ||
            (
              selectedContacts.length === 0 &&
              (!currentInput.value || !!currentInput.error)
            )
          }
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {isLoading ? 'Sharing...' : 'Share'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

FileManagerShareDialog.propTypes = {
  shared: PropTypes.array,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  setSelectedImages: PropTypes.func,
  fromCategory: PropTypes.bool,
  totalImages: PropTypes.array,
  fromProject: PropTypes.bool,
  selectedImages: PropTypes.array,
};